/**
 * Leads page with Kanban and List views (toggle persisted in localStorage).
 *
 * Kanban view (Round 18.2, the DEFAULT since round9/design-pass per the
 * 2026-09-08 Kanban-first decision): all leads grouped into five stage
 * columns; drag a card to change its stage (optimistic, rolls back on API
 * failure). List view: Active / Closed tabs (submissionStatus filter), one
 * row per lead with a stage badge.
 *
 * The page chrome (topbar, primary nav, Sign out) is rendered by
 * ProtectedRoute's TenantShell, not here.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import { listLeads, type LeadSummary, ApiError } from "../lib/api";
import { STAGE_LABELS, STAGE_COLORS, stageOf } from "../lib/leads";
import { isTenantUser } from "../lib/authRole";
import { homeownerDesignerUrl } from "../lib/designerLink";
import { KanbanBoard } from "../components/leads/KanbanBoard";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import ui from "../styles/ui.module.css";
import styles from "./LeadsPage.module.css";

type TabKey = "active" | "closed";
type ViewKey = "list" | "kanban";

function readStoredView(): ViewKey {
  try {
    const stored = localStorage.getItem("leads-view");
    return stored === "list" || stored === "kanban" ? stored : "kanban";
  } catch {
    return "kanban";
  }
}

export function LeadsPage() {
  const { user, signOut } = useAuth();
  const [view, setView] = useState<ViewKey>(readStoredView);
  const [activeTab, setActiveTab] = useState<TabKey>("active");
  const [leads, setLeads] = useState<LeadSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("leads-view", view);
    } catch {
      // Storage unavailable — the choice still holds for this mount.
    }
  }, [view]);

  const loadLeads = useCallback(
    async (status: TabKey | undefined, cursor?: string) => {
      if (!user) return;
      setError(null);
      setIsLoading(true);
      try {
        const result = await listLeads(user.idToken, { status, cursor });
        setLeads(prev => (cursor ? [...prev, ...result.items] : result.items));
        setNextCursor(result.nextCursor);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setError(friendlyErrorMessage(err, "loading your leads"));
      } finally {
        setIsLoading(false);
      }
    },
    [user, signOut],
  );

  // Kanban shows ALL leads grouped by stage; list view honors the Active/Closed tab.
  const effectiveStatus: TabKey | undefined = view === "kanban" ? undefined : activeTab;

  // When the filter changes, reset the loaded page + loading/error state DURING
  // RENDER (React's "adjusting state on a change" pattern) rather than inside
  // the effect — a synchronous setState-in-effect on this hot, builder-facing
  // page triggers a cascading render.
  const [loadedStatus, setLoadedStatus] = useState(effectiveStatus);
  if (loadedStatus !== effectiveStatus) {
    setLoadedStatus(effectiveStatus);
    setLeads([]);
    setNextCursor(null);
    setIsLoading(true);
    setError(null);
  }

  // Initial + on-filter-change load. Inlined (rather than calling loadLeads) so
  // every setState sits inside an async .then/.catch/.finally — calling
  // loadLeads here would introduce a setState reachable synchronously from the
  // effect (react-hooks/set-state-in-effect). The loading/error/reset flip for
  // this path is done during render above; loadLeads still owns retry + paging.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listLeads(user.idToken, { status: effectiveStatus })
      .then((result) => {
        if (cancelled) return;
        setLeads(result.items);
        setNextCursor(result.nextCursor);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setError(friendlyErrorMessage(err, "loading your leads"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [effectiveStatus, user, signOut]);

  // Replace a lead in the loaded set (Kanban optimistic update + server result).
  const updateLeadInList = useCallback((updated: LeadSummary) => {
    setLeads(prev =>
      prev.map(l =>
        l.idempotencyKey === updated.idempotencyKey ? { ...l, ...updated } : l,
      ),
    );
  }, []);

  // The builder's own live designer, for the empty state's "see what your
  // homeowners see" link. round11/portal-create-lead: this is the plain
  // homeowner URL (?builder=), NOT buildEmbedUrl — that helper always adds
  // preview=1, which mounts the embed's read-only viewer rather than the
  // designer (see lib/designerLink.ts). Null when the build has no embed
  // configured, in which case the link is simply not offered.
  const previewUrl = useMemo(() => {
    if (!user || !isTenantUser(user)) return null;
    return homeownerDesignerUrl(user.tenantId);
  }, [user]);

  if (!user) return null;

  const isEmpty = !isLoading && leads.length === 0 && !error;

  return (
    <main id="main-content" className={`${ui.main} ${styles.main}`}>
      <div className={ui.pageHeader}>
        <h1>Leads</h1>
        <div className={ui.actions}>
          <div className="view-toggle" role="group" aria-label="Leads view">
            <button
              type="button"
              className={view === "kanban" ? "active" : ""}
              aria-pressed={view === "kanban"}
              onClick={() => setView("kanban")}
            >
              Kanban
            </button>
            <button
              type="button"
              className={view === "list" ? "active" : ""}
              aria-pressed={view === "list"}
              onClick={() => setView("list")}
            >
              List
            </button>
          </div>
          {/* round11/portal-create-lead — the same action in both views: the
              header is shared, so the button is too. */}
          <Link to="/leads/new" className={ui.button}>New lead</Link>
        </div>
      </div>

      {error && (
        <StatusMessage variant="error" message={error} className={styles.error}>
          {" "}
          <button
            type="button"
            className={`${ui.buttonDanger} ${ui.buttonSmall}`}
            onClick={() => loadLeads(effectiveStatus)}
          >
            Retry
          </button>
        </StatusMessage>
      )}

      {view === "list" ? (
        <>
          <nav className={styles.tabs} aria-label="Lead status">
            <button
              type="button"
              className={`${styles.tab} ${activeTab === "active" ? styles.tabActive : ""}`}
              aria-pressed={activeTab === "active"}
              onClick={() => setActiveTab("active")}
            >
              Active
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === "closed" ? styles.tabActive : ""}`}
              aria-pressed={activeTab === "closed"}
              onClick={() => setActiveTab("closed")}
            >
              Closed
            </button>
          </nav>

          {isLoading && leads.length === 0 && (
            <p className={ui.loading}>Loading leads…</p>
          )}

          {isEmpty && (
            <p className={ui.empty}>
              {activeTab === "active" ? "No active leads yet." : "No closed leads yet."}
            </p>
          )}

          {leads.length > 0 && (
            <div className={ui.tableWrap}>
              <table className={`${ui.table} ${styles.leadsTable}`}>
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Homeowner</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th>Estimate</th>
                    {/* AUD-019 (axe: minor) — icon-only column (the "View"
                        link); visually-hidden text so it isn't announced
                        as a blank header. */}
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.idempotencyKey}>
                      <td className={styles.submittedCell}>{formatTimestamp(lead.submissionTimestamp)}</td>
                      <td>
                        <div className={styles.homeownerName}>
                          {lead.homeownerName}
                          {lead.source === "portal" && (
                            <span className={`${ui.badge} ${ui.badgeTan} ${styles.sourceBadge}`}>Added by your team</span>
                          )}
                        </div>
                        <div className={styles.homeownerContact}>{lead.homeownerEmail}</div>
                      </td>
                      <td>
                        <span
                          className="stage-badge"
                          style={{ background: STAGE_COLORS[stageOf(lead)] }}
                        >
                          {STAGE_LABELS[stageOf(lead)]}
                        </span>
                      </td>
                      <td>
                        <span className={`${ui.badge} ${statusTone(lead.submissionStatus)}`}>
                          {lead.submissionStatus}
                        </span>
                      </td>
                      <td className={styles.estimateCell}>
                        {lead.quoteResult
                          ? `$${lead.quoteResult.lowEstimate.toLocaleString()} – $${lead.quoteResult.highEstimate.toLocaleString()}`
                          : "—"}
                      </td>
                      <td className={styles.actionCell}>
                        <Link
                          to={`/leads/${encodeURIComponent(lead.idempotencyKey)}`}
                          className={styles.viewLink}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          {isLoading && leads.length === 0 && (
            <p className={ui.loading}>Loading leads…</p>
          )}
          {isEmpty && (
            <div className={`${ui.callout} ${styles.emptyCallout}`}>
              <p className={styles.emptyLead}>
                <strong>No leads yet.</strong> Leads arrive when a homeowner submits a
                design from the designer embedded on your website. Your board is ready
                for them.
              </p>
              <div className={ui.actions}>
                <Link to="/leads/new" className={`${ui.buttonSecondary} ${ui.buttonSmall}`}>
                  Add a lead yourself
                </Link>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${ui.buttonSecondary} ${ui.buttonSmall}`}
                  >
                    Preview your designer
                  </a>
                )}
                <Link to="/templates" className={`${ui.buttonSecondary} ${ui.buttonSmall}`}>
                  Set up templates
                </Link>
              </div>
            </div>
          )}
          {!isLoading && !error && (
            <KanbanBoard leads={leads} onLeadUpdated={updateLeadInList} />
          )}
        </>
      )}

      {nextCursor && !isLoading && (
        <button
          type="button"
          className={`${ui.buttonSecondary} ${styles.loadMoreButton}`}
          onClick={() => loadLeads(effectiveStatus, nextCursor)}
        >
          Load more
        </button>
      )}
    </main>
  );
}

// submissionStatus → badge tone. Text carries the value; colour reinforces.
function statusTone(status: LeadSummary["submissionStatus"]): string {
  switch (status) {
    case "acknowledged": return ui.badgeWarn;
    case "closed": return ui.badgeOk;
    default: return ui.badgeInfo;
  }
}

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
